import { useState, useRef, useEffect } from "react";

// ASSETS

import LeftArrow from "assets/icons/ArrowLeftHovered.svg";
import shareIcon from "assets/svg/ShareIcon.svg";
import ArrowUpIcon from "assets/svg/RightRedIcon.svg";
import CoverPlaceholder from "assets/img/CoverPlaceholder.png";
import facebookIcon from "assets/svg/FacebookGrey.svg";
import linkedInIcon from "assets/svg/LinkedInGrey.svg";

// STYLES
import {
  NewsAndPartnersDetailsContainer,
  HeaderImageWrapper,
  HeaderImage,
  Body,
  FirstColumn,
  SecondColumn,
  ThirdColumn,
  BackToButton,
  BackToImage,
  BackToWrapper,
  TitleWrapper,
  ContentCard,
  SocialMediaCard,
  SocialMediaIcons,
  SocialMediaIcon,
  OtherGuidesCard,
  ImgStyled,
  OtherChapters,
  InsideFirstColumn,
  InsideThirdColumn,
  BackOnTopWrapper,
  BackOnTopImg,
  LeftCard,
  OtherCampaignsContent,
  CopyToClipboardWrapper,
  SkeletonWrapperOtherChapter,
  CopyToClipboardTooltip,
  SkeletonWrapperChapter,
  LeftCardTitle,
  LeftCardChapter,
  RedCircleWrapper,
  RedCircle,
  ScrollTargetWrapper,
  CenterCardTitle,
  DownloadContentWrapper,
  DownloadFiles,
  TextWrapperOtherArticles,
} from "./ProjectDetailsStyled";

// LIBRARIES
import { useMediaQuery } from "react-responsive";
import { useNavigate, useParams } from "react-router-dom";
import { LinkedinShareButton, FacebookShareButton } from "react-share";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

// MISC

import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { BORDER_RADIUS, COLORS, DEVICE_TYPE } from "constants/variables";
import { appRoutes } from "config/AppRoutesConfig";
import { content } from "constants/mocks/ProjectData";
// REDUX

import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import CustomText from "components/Atoms/CustomText/CustomText";
import MarkdownText from "components/Atoms/MarkdownText/MarkdownText";
import OfficialsCarousel from "components/OfficialsCarousel/OfficialsCarousel";
import DownloadFileAtom from "components/Atoms/DownloadFileAtom/DownloadFileAtom";
import aboutService from "services/aboutService";
import { ListRootObject, RootObject } from "./ProjectDetailsModel";
import { uploadBaseUrl } from "services/base";
import StrapiTreeText from "components/Atoms/StrapiTreeText/StrapiTreeText";
import { DownloadFileType } from "components/Atoms/DownloadFileAtom/DownloadFileAtomModel";

const OfficialAnnouncementsDetails = () => {
  // PROPERTIES

  // LIBRARY CONSTANTS
  const isMobile = useMediaQuery({ maxWidth: DEVICE_TYPE.mobileHeader });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);

  // STATE CONSTANTS
  const [projectData, setProjectData] = useState<RootObject>();
  const [otherProjects, setOtherProjects] = useState<ListRootObject>();

  // STATE CONSTANTS

  const contentRefs = useRef([]);
  const [copied, setCopied] = useState(false);
  const { id } = useParams();

  const headerImageWrapperRef = useRef(null);
  const skeletonCount = 3;

  // EVENT HANDLERS

  const scrollToTop = () => {
    if (headerImageWrapperRef.current) {
      headerImageWrapperRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error("Failed to copy URL: ", err));
  };

  useEffect(() => {
    aboutService
      .getProjectById(Number(id))
      .then((response) => {
        // console.log("getProjectById response", response);
        setProjectData(response);
      })
      .catch((err) => {
        console.log("getProjectById error", err);
      });
  }, [id]);

  useEffect(() => {
    aboutService
      .getProjects()
      .then((response) => {
        // console.log("setOtherProjects response", response);
        setOtherProjects(response);
      })
      .catch((err) => {
        console.log("getProjects error", err);
      });
  }, []);

  return (
    <NewsAndPartnersDetailsContainer>
      <HeaderImageWrapper ref={headerImageWrapperRef}>
        <HeaderImage
          src={
            projectData?.data?.attributes?.cover?.data?.attributes?.url?.trim()
              ? `${uploadBaseUrl}${projectData?.data?.attributes?.cover?.data?.attributes?.url}`
              : CoverPlaceholder
          }
          alt={"coverGuideImage"}
          loading="lazy" decoding="async"
        />
      </HeaderImageWrapper>
      <Body>
        <FirstColumn>
          <InsideFirstColumn>
            <BackToWrapper
              onClick={() => navigate(appRoutes.projects)}
              biggerCursor={biggerCursor}
            >
              <BackToImage src={LeftArrow} alt="backIcon" />
              <BackToButton biggerCursor={biggerCursor}>
                <CustomText
                  text={t("view.projects.details.backButton")}
                  type={TextType.H4bold}
                  color={COLORS.RaisinBlack}
                />
              </BackToButton>
            </BackToWrapper>
            <TitleWrapper>
              <CustomText
                text={projectData?.data?.attributes?.title}
                type={TextType.H3}
              />
            </TitleWrapper>
          </InsideFirstColumn>
        </FirstColumn>
        <SecondColumn>
          {content !== null ? (
            <ScrollTargetWrapper
              ref={contentRefs.current[0]}
              // key={index}
              id={projectData?.data?.attributes?.title}
            >
              <ContentCard tabIndex={0}>
                <CenterCardTitle>
                  <CustomText
                    text={projectData?.data?.attributes?.title}
                    type={TextType.H3}
                  />
                </CenterCardTitle>
                <StrapiTreeText text={projectData?.data?.attributes?.content} />
              </ContentCard>
            </ScrollTargetWrapper>
          ) : (
            <ContentCard tabIndex={0}>
              <Skeleton
                width={"100%"}
                count={105}
                baseColor={COLORS.Background}
                highlightColor={COLORS.Cultured}
              />
            </ContentCard>
          )}
          <DownloadContentWrapper>
            <CustomText text={"Documente"} type={TextType.H3} />
            <DownloadFiles>
              {projectData?.data?.attributes?.documents?.data.map((doc) => {
                return (
                  <DownloadFileAtom
                    key={doc?.id}
                    type={DownloadFileType.Orders}
                    title={doc?.attributes?.title}
                    documentType={"Document"}
                    institution={"Document"}
                    date={new Date(
                      doc?.attributes?.publishedAt
                    ).toLocaleDateString("en-GB")}
                    pdfUrl={`${uploadBaseUrl}${doc?.attributes?.attached_document?.data?.attributes?.url}`}
                  />
                );
              })}
            </DownloadFiles>
          </DownloadContentWrapper>
        </SecondColumn>
        <ThirdColumn>
          <InsideThirdColumn>
            <SocialMediaCard>
              <CustomText
                text={"SHARE"}
                type={TextType.H5}
                color={COLORS.Yellow}
              />
              <SocialMediaIcons>
                <FacebookShareButton url={window.location.href}>
                  <SocialMediaIcon
                    src={facebookIcon}
                    alt="facebookIcon"
                    biggerCursor={biggerCursor}
                  />
                </FacebookShareButton>
                <LinkedinShareButton url={window.location.href}>
                  <SocialMediaIcon
                    src={linkedInIcon}
                    alt="linkedInIcon"
                    biggerCursor={biggerCursor}
                  />
                </LinkedinShareButton>
                <CopyToClipboardWrapper onClick={handleCopyToClipboard}>
                  <SocialMediaIcon
                    src={shareIcon}
                    alt="shareIcon"
                    biggerCursor={biggerCursor}
                  />
                  {copied && (
                    <CopyToClipboardTooltip>
                      <CustomText
                        text={t(
                          "view.newsAndMedia.officialAnnouncementsDetails.copyClipboard"
                        )}
                        type={TextType.P2}
                        color={COLORS.RaisinBlack}
                      />
                    </CopyToClipboardTooltip>
                  )}
                </CopyToClipboardWrapper>
              </SocialMediaIcons>
            </SocialMediaCard>
            <OtherGuidesCard>
              <CustomText
                text={t(
                  "view.newsAndMedia.officialAnnouncementsDetails.otherProjects"
                )}
                type={TextType.H3}
              />
              {otherProjects?.data.length > 0 ? (
                otherProjects?.data
                  .filter((project: any) => project?.id !== Number(id))
                  .slice(0, 3)
                  .map((project: any, index: number) => (
                    <OtherChapters
                      key={index}
                      biggerCursor={biggerCursor}
                      onClick={() =>
                        navigate(appRoutes.projectDetails + project?.id)
                      }
                    >
                      <ImgStyled
                        src={
                          project?.attributes?.cover?.data?.attributes?.url?.trim()
                            ? `${uploadBaseUrl}${project?.attributes?.cover?.data?.attributes?.url}`
                            : CoverPlaceholder
                        }
                        alt={"imageOthercampaigns"}
                      />
                      <OtherCampaignsContent>
                        <TextWrapperOtherArticles>
                          <CustomText
                            text={project?.attributes?.title}
                            type={TextType.H4officials}
                          />
                        </TextWrapperOtherArticles>
                      </OtherCampaignsContent>
                    </OtherChapters>
                  ))
              ) : (
                <>
                  {[...Array(skeletonCount)].map((_, index) => (
                    <SkeletonWrapperOtherChapter key={index}>
                      <Skeleton
                        width={"80px"}
                        height={"80px"}
                        borderRadius={BORDER_RADIUS.radius16}
                        baseColor={COLORS.Background}
                        highlightColor={COLORS.Cultured}
                      />
                      <SkeletonWrapperChapter>
                        <Skeleton
                          count={1}
                          baseColor={COLORS.Background}
                          highlightColor={COLORS.Cultured}
                        />
                      </SkeletonWrapperChapter>
                    </SkeletonWrapperOtherChapter>
                  ))}
                </>
              )}
            </OtherGuidesCard>
          </InsideThirdColumn>
        </ThirdColumn>
      </Body>
      {projectData?.data?.attributes?.gallery?.data && (
        <OfficialsCarousel
          data={projectData?.data?.attributes?.gallery?.data?.map((image) => ({
            id: image?.id,
            image: `${uploadBaseUrl}${image?.attributes?.url}`,
          }))}
        />
      )}
      {isMobile && (
        <BackOnTopWrapper onClick={scrollToTop}>
          <BackOnTopImg src={ArrowUpIcon} alt="ArrowUpIcon" />
        </BackOnTopWrapper>
      )}
    </NewsAndPartnersDetailsContainer>
  );
};

export default OfficialAnnouncementsDetails;
